import type { ApiClient, Query } from '@saas/ui';
import type { AuthenticationResponseJSON, PublicKeyCredentialCreationOptionsJSON, PublicKeyCredentialRequestOptionsJSON, RegistrationResponseJSON } from '@simplewebauthn/browser';
import type { BillingEstimate, DepartmentSummary, OnboardingStatus, RoleSummary, SubscriptionSummary, TenantSummary, TermsInfo, UserSummary } from '../api/core.dto.js';
import type { CompanyQuietHoursView, NotificationKindView, NotificationQuietHoursView, TenantExportChangesView } from '../api/core.dto.js';
import type { BusinessCalendarHolidays, BusinessCalendarSettingsPatch, BusinessCalendarView } from '../api/business-calendar.js';
import type { MailConnectionScopePatch, MailConnectionStartInput, MailConnectionSummary, MailConsentText, MailFolderInfo, MailOAuthCompleteResult, MailOAuthStartResult, MailProviderInfo, MailSyncState } from '../api/mail.dto.js';
import type { MailAdminSettings, MailConnectionAdminRow } from '../api/mail.dto.js';
import type { NotificationWebhookInput, NotificationWebhookPatch, NotificationWebhookSummary, NotificationWebhookTestResult } from '../api/external-notify.dto.js';

/**
 * core の HTTP 契約（ブラウザ側）。型は api/ の DTO を使い、サーバが行のまま返すもの（監査ログ・通知・配送・送信記録）は snake_case。
 * クエリキーはここで一元管理し、シェル（apps/web）も同じキーで件数を取る。
 */
export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

/** 配列で返す口と { items } で返す口を同じ形（配列）に揃える（SFA 1B のメール接続は配列が正） */
function listOf<T>(r: T[] | Page<T> | null | undefined): T[] {
  if (!r) return [];
  if (Array.isArray(r)) return r;
  return Array.isArray(r.items) ? r.items : [];
}

export interface UserWithRoles extends UserSummary {
  roleCodes: string[];
  lastLoginAt: string | null;
  createdAt: string;
}

export interface NotificationRow {
  id: string;
  user_id: string;
  kind: string;
  title: string;
  body: string | null;
  link: string | null;
  read_at: string | null;
  created_at: string;
}

export interface AuditLogRow {
  id: string;
  actor_id: string | null;
  actor_type?: 'user' | 'api_key' | 'system';
  action: string;
  target_type: string | null;
  target_id: string | null;
  before: unknown;
  after: unknown;
  ip: string | null;
  user_agent: string | null;
  created_at: string;
}

export interface ModuleInfo {
  name: string;
  displayName: string;
  plans: string[];
  includedInPlan: boolean;
  enabled: boolean;
  onDisabled: string;
}

export interface UsageSummaryRow {
  metric: string;
  quantity: string;
  cost: string;
  price: string;
}

export type FileQuotaBucket = 'total' | 'storage' | 'chat' | 'meeting' | 'expense' | 'contract' | 'workflow' | 'knowledge' | 'crm' | 'other';
export type FileQuotaMode = 'warn' | 'block';
export interface FileQuotaSummaryRow {
  bucket: FileQuotaBucket;
  label: string;
  usedBytes: string;
  pendingBytes: string;
  limitBytes: string | null;
  customLimitBytes: string | null;
  remainingBytes: string | null;
  percent: number | null;
  mode: FileQuotaMode;
  source: 'plan' | 'custom' | 'unlimited';
  hasOverride: boolean;
}

export interface EventDeliveryRow {
  event_id: string;
  handler: string;
  status: 'ok' | 'processing' | 'retry' | 'failed';
  attempts: number;
  error: string | null;
  next_attempt_at: string | null;
  created_at: string;
  updated_at: string;
  event_name: string;
  occurred_at: string;
}

export interface TenantExportRow {
  id: string;
  generated_at: string;
  row_counts: Record<string, number>;
  file_count: number;
  total_bytes: string;
  manifest_hash: string;
  storage_key: string;
  requested_by: string | null;
  /** 段 5: 非同期になった（03 §6.3 ②）。queued → running → ready / failed */
  status: 'queued' | 'running' | 'ready' | 'failed';
  kind: 'full' | 'diff';
  part_count: number;
  progress: { step?: string; filesDone?: number; filesTotal?: number; bytes?: number; parts?: number };
  error: string | null;
  requested_at: string;
  completed_at: string | null;
  /** 2026-10-03: 差分（kind = diff）の元にした完全なエクスポート */
  base_export_id?: string | null;
  /** 差分の起点（この時刻以降に追加・変更された分） */
  since?: string | null;
  /** 集計を始めた時刻（この時点のデータ。次の差分の起点になる） */
  data_as_of?: string | null;
}

// ---- 2026-10-03: 深夜の通知抑制・通知の種類の一覧・差分エクスポート ----
export type { CompanyQuietHoursView, NotificationQuietHoursView, NotificationKindView, TenantExportChangesView };

export interface ExportFile {
  fileId: string;
  filename: string;
  sizeBytes?: number | null;
  url: string;
}

// ---- 段 5: 契約と席数・初期設定（02 §5.1・02 §9.3）----
export type { SubscriptionSummary, BillingEstimate, OnboardingStatus, TermsInfo };

// ---- SFA 1A: 営業日カレンダー（SFA機能拡張設計書 §15.2.1）----
export type { BusinessCalendarView, BusinessCalendarHolidays, BusinessCalendarSettingsPatch };

// ---- SFA 段階 5（§9.6）: 社外チャット（Slack / Teams Workflows）の送り先（/admin/notification-webhooks・core.integration.manage）----
export type { NotificationWebhookSummary, NotificationWebhookInput, NotificationWebhookPatch, NotificationWebhookTestResult };

/** 端末台帳の 1 行（GET /devices）。push_token は返さない */
export interface UserDeviceRow {
  id: string;
  userId: string;
  platform: 'web' | 'ios' | 'android';
  capabilities: string[];
  userAgent: string | null;
  lastSeenAt: string | null;
  createdAt: string;
}

// ---- 段 5: 通知の設定（GET/PUT /notifications/preferences）とバッジ（GET /badge）----

export type NotificationCategory = 'approval' | 'mention' | 'deadline' | 'alert' | 'safety' | 'security' | 'system';
export type NotificationChannel = 'push' | 'email';
/** daily = まとめ送り（テナントの朝 8 時に 1 通）。email だけ */
export type NotificationMode = 'on' | 'off' | 'daily';

export interface NotificationPreferenceRow {
  category: NotificationCategory;
  label: string;
  description: string;
  /** 安否確認・セキュリティは切れない */
  locked: boolean;
  push: { mode: NotificationMode; default: NotificationMode };
  email: { mode: NotificationMode; default: NotificationMode; allowsDaily: boolean };
  /** 2026-10-03: この区分に含まれる通知の名前（モジュールが登録した種類も含む）。古いサーバは返さない */
  kinds?: string[];
}

export interface NotificationPreferences {
  items: NotificationPreferenceRow[];
  /** まとめ送りが届く時刻（テナントのタイムゾーン） */
  digest: { hour: number; timeZone: string };
}

/** GET /badge（04 §10.3。アプリもこの口を使う） */
export interface BadgeSummary {
  count: number;
  breakdown: { unreadDm: number; mentions: number; approvals: number };
}

/** 通知テンプレート（GET /admin/notification-templates） */
export interface NotificationTemplateRow {
  placeholders: string[];
  requiredPlaceholders: string[];
  code: string;
  channel: 'app' | 'email' | 'chat';
  label: string;
  category: NotificationCategory;
  categoryLabel: string;
  subject: string;
  body: string;
  defaultSubject: string;
  defaultBody: string;
  overridden: boolean;
  updatedAt: string | null;
}

/** メール / SMS の送信記録（GET /admin/outbound-messages） */
export interface OutboundMessageRow {
  delivery_status?:string|null;
  delivery_error_code?:string|null;
  delivered_at?:string|null;
  id: string;
  channel: 'email' | 'sms';
  recipient: string;
  subject: string | null;
  body: string;
  kind: string;
  ref_type: string | null;
  ref_id: string | null;
  status: 'sent' | 'failed' | 'logged';
  provider: string;
  provider_message_id: string | null;
  error: string | null;
  created_at: string;
  sent_at: string | null;
}

/** 在席状態（GET /presence・段 4）。形は api/ の PresenceSummary（日時は文字列） */
export interface PresenceRow {
  userId: string;
  state: 'online' | 'away' | 'dnd' | 'offline';
  activity: 'on_call' | 'in_meeting' | 'in_chat' | 'idle' | null;
  activitySrc: string | null;
  activityRef: string | null;
  manual: boolean;
  since: string;
  autoUntil: string | null;
  device: string | null;
  manualState: 'online' | 'away' | 'dnd' | 'offline' | null;
  manualUntil: string | null;
}

/** パスワード再設定リンクの中身（GET /auth/password-reset/:token）。無効でも 200 で valid = false が返る */
export interface PasswordResetPeekRow {
  valid: boolean;
  tenantName: string | null;
  /** 伏せ字（t***a@e***.co.jp） */
  email: string | null;
  expiresAt: string | null;
}

/** 招待の中身（GET /auth/invitations/:token） */
export interface InvitationPeekRow {
  email: string;
  displayName: string;
  tenant: { id: string; slug: string; name: string };
  expiresAt: string;
  alreadyActive: boolean;
}

export interface UserListQuery {
  status?: 'invited' | 'active' | 'suspended';
  departmentId?: string;
  search?: string;
  limit?: number;
  cursor?: string;
}

export interface AuditQuery {
  action?: string;
  targetType?: string;
  targetId?: string;
  actorId?: string;
  from?: string;
  to?: string;
  limit?: number;
  cursor?: string;
}

export interface OutboundMessagesQuery {
  channel?: 'email' | 'sms';
  status?: 'sent' | 'failed' | 'logged';
  kind?: string;
  recipient?: string;
  limit?: number;
  cursor?: string;
}

// ---- 段 5: 2 要素認証・セッション・外部ログイン（02 §3.1・04 §6.2・05 AUTH-05）----

export interface TwoFactorStatusRow {
  enabled: boolean;
  pending: boolean;
  confirmedAt: string | null;
  recoveryCodesRemaining: number;
  required: boolean;
}

export interface TwoFactorSetupRow {
  secret: string;
  otpauthUri: string;
  issuer: string;
  account: string;
}

export interface SessionRow {
  id: string;
  client: string;
  userAgent: string | null;
  deviceId: string | null;
  ip: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string;
  current: boolean;
}

export interface AuthProviderRow {
  provider: 'google' | 'microsoft';
  displayName: string;
}

export interface IdentityRow {
  provider: string;
  email: string | null;
  createdAt: string;
  lastLoginAt: string | null;
}

export interface PasskeyRow {
  id: string;
  name: string;
  backedUp: boolean;
  createdAt: string;
  lastUsedAt: string | null;
}

/** POST /auth/login が 2 要素認証を要求したときの応答（トークンは入っていない） */
export interface TwoFactorChallengeRow {
  twoFactorRequired: true;
  twoFactorToken: string;
  expiresInSec: number;
}

export const coreKeys = {
  me: () => ['auth', 'me'] as const,
  twoFactor: () => ['auth', '2fa'] as const,
  sessions: () => ['auth', 'sessions'] as const,
  identities: () => ['auth', 'identities'] as const,
  passkeys: () => ['auth', 'passkeys'] as const,
  authProviders: () => ['auth', 'providers'] as const,
  userTwoFactor: (id: string) => ['admin', 'users', id, '2fa'] as const,
  userSessions: (id: string) => ['admin', 'users', id, 'sessions'] as const,
  tenantPublic: (slug: string | null) => ['auth', 'tenant', slug] as const,
  invitation: (token: string) => ['auth', 'invitation', token] as const,
  passwordReset: (token: string) => ['auth', 'password-reset', token] as const,
  unreadCount: () => ['notifications', 'unread-count'] as const,
  notifications: (unreadOnly: boolean) => ['notifications', 'list', { unreadOnly }] as const,
  devices: () => ['devices'] as const,
  pushPublicKey: () => ['push', 'public-key'] as const,
  notificationPreferences: () => ['notifications', 'preferences'] as const,
  /** 2026-10-03: 深夜の通知抑制（本人） */
  notificationQuietHours: () => ['notifications', 'quiet-hours'] as const,
  /** 2026-10-03: 深夜の通知抑制（会社の既定）と通知の種類の一覧（管理画面） */
  companyQuietHours: () => ['admin', 'notification-quiet-hours'] as const,
  notificationKinds: () => ['admin', 'notification-kinds'] as const,
  badge: () => ['badge'] as const,
  notificationTemplates: (channel: string) => ['admin', 'notification-templates', channel] as const,
  /** SFA 段階 5: 社外チャットの送り先（URL は含まれない） */
  notificationWebhooks: () => ['admin', 'notification-webhooks'] as const,
  users: (q: UserListQuery) => ['admin', 'users', q] as const,
  user: (id: string) => ['admin', 'users', 'detail', id] as const,
  departments: () => ['admin', 'departments'] as const,
  roles: () => ['admin', 'roles'] as const,
  permissions: () => ['admin', 'permissions'] as const,
  tenant: () => ['admin', 'tenant'] as const,
  /** SFA 1A: 営業日カレンダー（year は同梱の祝日の年） */
  businessCalendar: (year: number | null) => ['admin', 'business-calendar', year ?? 'current'] as const,
  /** SFA 1A: 休みの一覧（ログイン済みなら誰でも。管理画面の保存で ['business-calendar'] ごと無効化する） */
  businessCalendarHolidays: (year: number | null) => ['business-calendar', 'holidays', year ?? 'current'] as const,
  currentIp: () => ['admin', 'security', 'current-ip'] as const,
  modules: () => ['admin', 'modules'] as const,
  auditLogs: (q: AuditQuery) => ['admin', 'audit-logs', q] as const,
  usage: (month: string) => ['admin', 'usage', month] as const,
  fileQuotas: () => ['admin', 'file-quotas'] as const,
  deliveries: (status: string | undefined) => ['admin', 'event-deliveries', status ?? 'all'] as const,
  outboundMessages: (q: OutboundMessagesQuery) => ['admin', 'outbound-messages', q] as const,
  exports: () => ['admin', 'exports'] as const,
  exportFiles: (id: string) => ['admin', 'exports', id, 'files'] as const,
  /** 2026-10-03: 受領確認のあとに増減した件数と差分の元（['admin','exports'] の無効化で一緒に取り直す） */
  exportChanges: () => ['admin', 'exports', 'changes'] as const,
  subscription: () => ['admin', 'subscription'] as const,
  billingEstimate: (month: string) => ['admin', 'billing', 'estimate', month] as const,
  onboarding: () => ['admin', 'onboarding'] as const,
  terms: () => ['auth', 'terms'] as const,
  myPresence: () => ['presence', 'me'] as const,
  // ---- SFA 1B: メールの接続（本人用・/settings/mail-connections）。['mail'] で一括 invalidate できる（crm のメール確認も同じキーを使う）----
  mailProviders: () => ['mail', 'providers'] as const,
  mailConsent: () => ['mail', 'consent'] as const,
  mailConnections: () => ['mail', 'connections'] as const,
  /** フォルダの一覧は事業者に問い合わせるので、接続の一覧（['mail','connections']）の無効化では取り直さない */
  mailFolders: (connectionId: string) => ['mail', 'folders', connectionId] as const,
  mailSync: (connectionId: string) => ['mail', 'sync', connectionId] as const,
  // gap-mail: メール接続の管理画面（/admin/mail-connections）
  adminMailConnections: () => ['mail', 'admin', 'connections'] as const,
  adminMailSettings: () => ['mail', 'admin', 'settings'] as const,
};

export const coreApi = {
  // サブドメインで会社が決まらない環境では ?slug= でも渡す（本番 API は X-Tenant ヘッダを見ない）
  tenantPublic: (api: ApiClient, slug?: string | null) =>
    api.get<{ id: string; slug: string; name: string }>('/auth/tenant', slug ? { slug } : undefined, { skipAuthRefresh: true, headers: slug ? { 'X-Tenant': slug } : undefined }),
  changePassword: (api: ApiClient, body: { currentPassword: string; newPassword: string }) => api.post<{ ok: true }>('/auth/password', body),

  // Passkeys (WebAuthn)
  passkeys: (api: ApiClient) => api.get<PasskeyRow[]>('/auth/passkeys'),
  passkeyRegistrationOptions: (api: ApiClient) =>
    api.post<{ challengeId: string; options: PublicKeyCredentialCreationOptionsJSON }>('/auth/passkeys/registration/options'),
  passkeyRegistrationVerify: (api: ApiClient, body: { challengeId: string; name?: string; response: RegistrationResponseJSON }) =>
    api.post<PasskeyRow>('/auth/passkeys/registration/verify', body),
  removePasskey: (api: ApiClient, id: string) => api.del<{ ok: true }>(`/auth/passkeys/${id}`),
  passkeyAuthenticationOptions: (api: ApiClient, tenantSlug?: string | null) =>
    api.post<{ challengeId: string; options: PublicKeyCredentialRequestOptionsJSON }>(
      '/auth/passkeys/authentication/options',
      tenantSlug ? { tenantSlug } : {},
      { skipAuthRefresh: true },
    ),
  passkeyAuthenticationVerify: (api: ApiClient, body: { challengeId: string; response: AuthenticationResponseJSON; tenantSlug?: string | null }) =>
    api.post<{ accessToken: string; refreshToken: string; expiresInSec: number; me: unknown }>('/auth/passkeys/authentication/verify', body, { skipAuthRefresh: true }),

  // 段 5: 2 要素認証（本人）。secret / otpauthUri / recoveryCodes は「その 1 回だけ」返る（保存しないこと）
  twoFactor: (api: ApiClient) => api.get<TwoFactorStatusRow>('/auth/2fa'),
  twoFactorSetup: (api: ApiClient) => api.post<TwoFactorSetupRow>('/auth/2fa/setup'),
  twoFactorEnable: (api: ApiClient, code: string) => api.post<{ recoveryCodes: string[] }>('/auth/2fa/enable', { code }),
  twoFactorDisable: (api: ApiClient, body: { password: string; code?: string; recoveryCode?: string }) => api.post<{ ok: true }>('/auth/2fa/disable', body),
  twoFactorRecoveryCodes: (api: ApiClient, body: { code?: string; recoveryCode?: string }) => api.post<{ recoveryCodes: string[] }>('/auth/2fa/recovery-codes', body),
  /** ログインの 2 段目。ここでトークンが出る（ログイン前なのでリフレッシュはしない） */
  twoFactorVerify: (api: ApiClient, body: { twoFactorToken: string; code?: string; recoveryCode?: string }) =>
    api.post<{ accessToken: string; refreshToken: string; expiresInSec: number; me: unknown }>('/auth/2fa/verify', body, { skipAuthRefresh: true }),

  // 段 5: ログイン中の端末（04 §6.2）
  sessions: (api: ApiClient) => api.get<SessionRow[]>('/auth/sessions'),
  revokeSession: (api: ApiClient, id: string) => api.del<{ ok: true; revoked: number }>(`/auth/sessions/${id}`),
  revokeOtherSessions: (api: ApiClient) => api.post<{ ok: true; revoked: number }>('/auth/sessions/revoke-others'),

  // 段 5: Google / Microsoft ログイン
  authProviders: (api: ApiClient) => api.get<AuthProviderRow[]>('/auth/providers', undefined, { skipAuthRefresh: true }),
  identities: (api: ApiClient) => api.get<IdentityRow[]>('/auth/identities'),
  unlinkIdentity: (api: ApiClient, provider: string) => api.post<{ ok: true }>(`/auth/identities/${provider}/unlink`),
  chooseOidcTenant: (api: ApiClient, body: { choiceToken: string; tenantId: string }) =>
    api.post<{ accessToken: string; refreshToken: string; expiresInSec: number; me: unknown }>('/auth/oidc/choose', body, { skipAuthRefresh: true }),

  // 段 5: 管理者から見たセキュリティ（core.user.manage）
  userTwoFactor: (api: ApiClient, id: string) => api.get<TwoFactorStatusRow>(`/admin/users/${id}/2fa`),
  resetUserTwoFactor: (api: ApiClient, id: string) => api.post<{ ok: true }>(`/admin/users/${id}/2fa/reset`),
  userSessions: (api: ApiClient, id: string) => api.get<SessionRow[]>(`/admin/users/${id}/sessions`),
  revokeUserSession: (api: ApiClient, id: string, sessionId: string) => api.del<{ ok: true; revoked: number }>(`/admin/users/${id}/sessions/${sessionId}`),
  revokeAllUserSessions: (api: ApiClient, id: string) => api.post<{ ok: true; revoked: number }>(`/admin/users/${id}/sessions/revoke-all`),
  resendInvitation: (api: ApiClient, id: string) => api.post<{ ok: true; expiresAt: string; status: string }>(`/admin/users/${id}/resend-invitation`),

  // パスワード再設定（AUTH-07・ログイン前）。要求は「あるともないとも言わない」ので常に成功で返る
  requestPasswordReset: (api: ApiClient, body: { email: string; tenantSlug?: string }) => api.post<{ ok: true }>('/auth/password-reset/request', body, { skipAuthRefresh: true }),
  passwordReset: (api: ApiClient, token: string) => api.get<PasswordResetPeekRow>(`/auth/password-reset/${encodeURIComponent(token)}`, undefined, { skipAuthRefresh: true }),
  confirmPasswordReset: (api: ApiClient, token: string, newPassword: string) =>
    api.post<{ ok: true; tenantSlug: string; email: string }>(`/auth/password-reset/${encodeURIComponent(token)}`, { newPassword }, { skipAuthRefresh: true }),

  // 招待の受諾（ログイン前・トークンがテナントを含むのでヘッダは要らない）
  invitation: (api: ApiClient, token: string) => api.get<InvitationPeekRow>(`/auth/invitations/${encodeURIComponent(token)}`, undefined, { skipAuthRefresh: true }),
  acceptInvitation: (api: ApiClient, token: string, password: string) =>
    api.post<{ tenantSlug: string; email: string }>(`/auth/invitations/${encodeURIComponent(token)}/accept`, { password }, { skipAuthRefresh: true }),

  notifications: (api: ApiClient, q: { unreadOnly: boolean; cursor?: string; limit?: number }) =>
    api.get<Page<NotificationRow>>('/notifications', { unreadOnly: q.unreadOnly || undefined, cursor: q.cursor, limit: q.limit ?? 30 }),
  unreadCount: (api: ApiClient) => api.get<{ count: number }>('/notifications/unread-count'),
  markRead: (api: ApiClient, id: string) => api.post<{ ok: boolean }>(`/notifications/${id}/read`),
  markAllRead: (api: ApiClient) => api.post<{ count: number }>('/notifications/read-all'),

  // 端末台帳（自分の端末だけ）と Web Push の公開鍵
  devices: (api: ApiClient) => api.get<UserDeviceRow[]>('/devices'),
  registerDevice: (api: ApiClient, body: { platform: 'web' | 'ios' | 'android'; pushToken: string; capabilities?: string[] }) => api.post<UserDeviceRow>('/devices', body),
  deleteDevice: (api: ApiClient, id: string) => api.del<{ ok: true }>(`/devices/${id}`),
  pushPublicKey: (api: ApiClient) => api.get<{ publicKey: string | null }>('/push/public-key'),

  // 段 5: 種類ごとの通知の ON/OFF（05 SET-03）とサーバが計算したバッジ（04 §10.3）
  notificationPreferences: (api: ApiClient) => api.get<NotificationPreferences>('/notifications/preferences'),
  updateNotificationPreferences: (api: ApiClient, items: Array<{ category: NotificationCategory; channel: NotificationChannel; mode: NotificationMode }>) =>
    api.put<NotificationPreferences>('/notifications/preferences', { items }),
  badge: (api: ApiClient) => api.get<BadgeSummary>('/badge'),

  // 2026-10-03: 深夜の通知抑制（本人: core.notification.read / 会社の既定: core.tenant.read・manage）と通知の種類の一覧
  notificationQuietHours: (api: ApiClient) => api.get<NotificationQuietHoursView>('/notifications/quiet-hours'),
  updateNotificationQuietHours: (api: ApiClient, body: { mode: 'inherit' | 'custom' | 'off'; start?: string; end?: string }) =>
    api.put<NotificationQuietHoursView>('/notifications/quiet-hours', body),
  companyQuietHours: (api: ApiClient) => api.get<CompanyQuietHoursView>('/admin/notification-quiet-hours'),
  updateCompanyQuietHours: (api: ApiClient, body: { enabled: boolean; start: string; end: string }) => api.put<CompanyQuietHoursView>('/admin/notification-quiet-hours', body),
  notificationKinds: (api: ApiClient) => api.get<NotificationKindView[]>('/admin/notification-kinds'),

  users: (api: ApiClient, q: UserListQuery) => api.get<Page<UserWithRoles>>('/admin/users', q as Query),
  user: (api: ApiClient, id: string) => api.get<UserWithRoles>(`/admin/users/${id}`),
  inviteUser: (api: ApiClient, body: unknown) => api.post<UserWithRoles>('/admin/users', body),
  updateUser: (api: ApiClient, id: string, body: unknown) => api.patch<UserWithRoles>(`/admin/users/${id}`, body),
  resetPassword: (api: ApiClient, id: string, newPassword: string) => api.post<{ ok: true }>(`/admin/users/${id}/reset-password`, { newPassword }),

  departments: (api: ApiClient) => api.get<DepartmentSummary[]>('/admin/departments'),
  createDepartment: (api: ApiClient, body: unknown) => api.post<DepartmentSummary>('/admin/departments', body),
  updateDepartment: (api: ApiClient, id: string, body: unknown) => api.patch<DepartmentSummary>(`/admin/departments/${id}`, body),
  deleteDepartment: (api: ApiClient, id: string) => api.del<{ ok: true }>(`/admin/departments/${id}`),

  roles: (api: ApiClient) => api.get<RoleSummary[]>('/admin/roles'),
  permissions: (api: ApiClient) => api.get<string[]>('/admin/permissions'),
  createRole: (api: ApiClient, body: unknown) => api.post<RoleSummary>('/admin/roles', body),
  updateRole: (api: ApiClient, id: string, body: unknown) => api.patch<RoleSummary>(`/admin/roles/${id}`, body),
  deleteRole: (api: ApiClient, id: string) => api.del<{ ok: true }>(`/admin/roles/${id}`),

  tenant: (api: ApiClient) => api.get<TenantSummary>('/admin/tenant'),
  currentIp: (api: ApiClient) => api.get<{ currentIp: string | null }>('/admin/security/ip'),
  updateTenant: (api: ApiClient, body: { name?: string; settings?: Record<string, unknown> }) => api.patch<TenantSummary>('/admin/tenant', body),
  modules: (api: ApiClient) => api.get<ModuleInfo[]>('/admin/modules'),

  // SFA 1A: 営業日カレンダー（GET は core.tenant.read・PUT は core.tenant.manage。holidays はログイン済みなら誰でも）
  businessCalendar: (api: ApiClient, year?: number | null) => api.get<BusinessCalendarView>('/admin/business-calendar', year ? { year } : undefined),
  updateBusinessCalendar: (api: ApiClient, body: BusinessCalendarSettingsPatch, year?: number | null) =>
    api.put<BusinessCalendarView>('/admin/business-calendar', body, year ? { query: { year } } : undefined),
  businessCalendarHolidays: (api: ApiClient, year?: number | null) => api.get<BusinessCalendarHolidays>('/business-calendar/holidays', year ? { year } : undefined),

  auditLogs: (api: ApiClient, q: AuditQuery) => api.get<Page<AuditLogRow>>('/admin/audit-logs', q as Query),
  usage: (api: ApiClient, month: string) => api.get<UsageSummaryRow[]>('/admin/usage', { month }),
  fileQuotas: (api: ApiClient) => api.get<FileQuotaSummaryRow[]>('/admin/file-quotas'),
  saveFileQuota: (api: ApiClient, bucket: FileQuotaBucket, body: { limitGb: number | null; mode: FileQuotaMode }) =>
    api.put<FileQuotaSummaryRow[]>(`/admin/file-quotas/${bucket}`, body),
  resetFileQuota: (api: ApiClient, bucket: FileQuotaBucket) => api.del<FileQuotaSummaryRow[]>(`/admin/file-quotas/${bucket}`),
  deliveries: (api: ApiClient, status?: string, limit = 100) => api.get<EventDeliveryRow[]>('/admin/event-deliveries', { status, limit }),
  outboundMessages: (api: ApiClient, q: OutboundMessagesQuery) => api.get<Page<OutboundMessageRow>>('/admin/outbound-messages', q as Query),

  // 通知テンプレートのテナント上書き（段 5・gap core A23）
  notificationTemplates: (api: ApiClient, channel?: 'app' | 'email' | 'chat') => api.get<NotificationTemplateRow[]>('/admin/notification-templates', { channel }),
  saveNotificationTemplate: (api: ApiClient, body: { code: string; channel: 'app' | 'email' | 'chat'; subject: string; body: string }) =>
    api.put<NotificationTemplateRow>('/admin/notification-templates', body),
  resetNotificationTemplate: (api: ApiClient, channel: 'app' | 'email' | 'chat', code: string) =>
    api.del<NotificationTemplateRow>(`/admin/notification-templates/${channel}/${encodeURIComponent(code)}`),

  // SFA 段階 5（§9.6）: 社外チャット（Slack / Teams Workflows）の送り先。URL は保存後に見えない（urlHint だけ）。すべて core.integration.manage
  notificationWebhooks: (api: ApiClient) => api.get<NotificationWebhookSummary[]>('/admin/notification-webhooks'),
  createNotificationWebhook: (api: ApiClient, body: NotificationWebhookInput) => api.post<NotificationWebhookSummary>('/admin/notification-webhooks', body),
  updateNotificationWebhook: (api: ApiClient, id: string, body: NotificationWebhookPatch) => api.patch<NotificationWebhookSummary>(`/admin/notification-webhooks/${id}`, body),
  deleteNotificationWebhook: (api: ApiClient, id: string) => api.del<{ ok: true }>(`/admin/notification-webhooks/${id}`),
  /** テスト送信（種類と件数だけの文面が届く）。失敗の回数には数えない */
  testNotificationWebhook: (api: ApiClient, id: string) => api.post<NotificationWebhookTestResult>(`/admin/notification-webhooks/${id}/test`),

  requestTermination: (api: ApiClient) => api.post<{ ok: true }>('/admin/termination'),
  /** kind = diff は前回の完全なエクスポート以降に追加・変更された分だけ（2026-10-03） */
  generateExport: (api: ApiClient, kind: 'full' | 'diff' = 'full') => api.post<TenantExportRow>('/admin/exports', { kind }),
  exports: (api: ApiClient) => api.get<TenantExportRow[]>('/admin/exports'),
  exportChanges: (api: ApiClient) => api.get<TenantExportChangesView>('/admin/exports/changes'),
  exportFiles: (api: ApiClient, id: string) => api.get<ExportFile[]>(`/admin/exports/${id}/files`),
  confirmExport: (api: ApiClient, id: string) => api.post<{ purgeScheduledOn: string }>(`/admin/exports/${id}/confirm`),
  retryExport: (api: ApiClient, id: string) => api.post<TenantExportRow>(`/admin/exports/${id}/retry`),

  // 段 5: 契約と席数（02 §5.1・01 §4.1b）。決済事業者との連携は無い（請求できる数字を作るだけ）
  subscription: (api: ApiClient) => api.get<SubscriptionSummary>('/admin/subscription'),
  changePlan: (api: ApiClient, plan: 'light' | 'standard' | 'pro') => api.post<SubscriptionSummary>('/admin/subscription/plan', { plan }),
  billingEstimate: (api: ApiClient, month: string) => api.get<BillingEstimate>('/admin/billing/estimate', { month }),

  // 段 5: 初期設定のチェックリスト（02 §9.3）
  onboarding: (api: ApiClient) => api.get<OnboardingStatus>('/admin/onboarding'),
  onboardingAct: (api: ApiClient, body: { action: 'check' | 'uncheck' | 'skip' | 'resume'; key?: string }) => api.post<OnboardingStatus>('/admin/onboarding', body),

  // 段 5: 利用規約とプライバシーポリシー（01 §9 #8/#10）
  terms: (api: ApiClient) => api.get<TermsInfo>('/auth/terms', undefined, { skipAuthRefresh: true }),
  acceptTerms: (api: ApiClient) => api.post<TermsInfo>('/auth/terms/accept'),

  fileUrl: (api: ApiClient, fileId: string, ttl = 900) => api.get<{ url: string }>(`/files/${fileId}/url`, { ttl }),

  // 在席状態（段 4）。他人の分は @saas/ui の usePresence() が GET /presence をまとめて呼ぶ
  myPresence: (api: ApiClient) => api.get<PresenceRow>('/presence/me'),
  setMyPresence: (api: ApiClient, body: { state: 'online' | 'away' | 'dnd' | 'offline'; manualUntil?: string | null }) => api.put<PresenceRow>('/presence/me', body),
  clearMyPresence: (api: ApiClient) => api.del<PresenceRow>('/presence/me'),

  // ---- SFA 1B: メールの接続（SFA機能拡張設計書 §6.1・§15.2.6。core.mail.connect・本人の接続だけ。他人の接続 ID は 404）----
  /** 事業者（configured = OAuth のクライアントが揃っている。fake は MAIL_FAKE_PROVIDER=true の環境だけ返る） */
  mailProviders: async (api: ApiClient) => listOf(await api.get<MailProviderInfo[] | Page<MailProviderInfo>>('/mail-connections/providers')),
  /** 同意文（版と行）。接続の開始に版をそのまま返す */
  mailConsent: (api: ApiClient) => api.get<MailConsentText>('/mail-connections/consent'),
  mailConnections: async (api: ApiClient) => listOf(await api.get<MailConnectionSummary[] | Page<MailConnectionSummary>>('/mail-connections')),
  mailConnection: (api: ApiClient, id: string) => api.get<MailConnectionSummary>(`/mail-connections/${id}`),
  /** 同意画面へ。戻り先は ${APP_URL}/settings/mail-connections/oauth/<provider>（画面が code / state を complete に渡す） */
  startMailOAuth: (api: ApiClient, provider: 'google' | 'microsoft', body: MailConnectionStartInput) => api.post<MailOAuthStartResult>(`/mail-connections/oauth/${provider}/start`, body),
  completeMailOAuth: (api: ApiClient, provider: 'google' | 'microsoft', body: { code: string; state: string }) => api.post<MailOAuthCompleteResult>(`/mail-connections/oauth/${provider}/complete`, body),
  /** 検証用の fake 事業者（MAIL_FAKE_PROVIDER=true のときだけ。本番には出ない） */
  connectFakeMail: (api: ApiClient, body: { accountEmail: string; fixture?: string; initialDays: number; consentVersion: string }) => api.post<MailConnectionSummary>('/mail-connections/fake', body),
  mailFolders: async (api: ApiClient, id: string) => listOf(await api.get<MailFolderInfo[] | Page<MailFolderInfo>>(`/mail-connections/${id}/folders`)),
  /** 取り込む範囲（フォルダ・除外アドレス・社内だけ・初回の日数）。期間の変更は画面で件数の目安を示してから送る */
  updateMailScope: (api: ApiClient, id: string, body: MailConnectionScopePatch) => api.patch<MailConnectionSummary>(`/mail-connections/${id}/scope`, body),
  resyncMail: (api: ApiClient, id: string) => api.post<unknown>(`/mail-connections/${id}/resync`),
  stopMail: (api: ApiClient, id: string) => api.post<unknown>(`/mail-connections/${id}/stop`),
  resumeMail: (api: ApiClient, id: string) => api.post<unknown>(`/mail-connections/${id}/resume`),
  /** 切断: 認証情報と本人用の取り込み（未共有の候補・本文のキャッシュ・除外）を消す。共有済みの業務記録（crm）は残る */
  disconnectMail: (api: ApiClient, id: string) => api.del<unknown>(`/mail-connections/${id}`),
  /** フォルダごとの同期の状態（最終成功・再試行・未復旧の期間） */
  mailSync: async (api: ApiClient, id: string) => listOf(await api.get<MailSyncState[] | Page<MailSyncState>>(`/mail-connections/${id}/sync`)),
  /** gap-mail: 共有メールボックス（Microsoft 365・Mail.Read.Shared の許可が要る）。足す前にサーバが読めるかを確かめる */
  addSharedMailbox: (api: ApiClient, id: string, address: string) => api.post<MailConnectionSummary>(`/mail-connections/${id}/shared-mailboxes`, { address }),
  removeSharedMailbox: (api: ApiClient, id: string, mailboxId: string) => api.del<MailConnectionSummary>(`/mail-connections/${id}/shared-mailboxes/${mailboxId}`),
  // gap-mail: 管理画面（core.mail.connection.manage・状態の確認と停止・削除だけ。件名・本文は返らない）
  adminMailConnections: async (api: ApiClient) => listOf(await api.get<MailConnectionAdminRow[] | Page<MailConnectionAdminRow>>('/admin/mail-connections')),
  adminMailSettings: (api: ApiClient) => api.get<MailAdminSettings>('/admin/mail-connections/settings'),
  saveAdminMailSettings: (api: ApiClient, body: { enabled: boolean }) => api.put<MailAdminSettings>('/admin/mail-connections/settings', body),
  adminStopMail: (api: ApiClient, id: string) => api.post<MailConnectionAdminRow>(`/admin/mail-connections/${id}/stop`),
  adminResumeMail: (api: ApiClient, id: string) => api.post<MailConnectionAdminRow>(`/admin/mail-connections/${id}/resume`),
  adminDeleteMail: (api: ApiClient, id: string) => api.del<MailConnectionAdminRow>(`/admin/mail-connections/${id}`),
};
